import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useSeo } from '@/hooks/useSeo';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function CheckoutSuccessPage() {
  useSeo({
    title: 'Ordine confermato',
    description: 'Il tuo ordine Ciro Gallotti è stato confermato. Grazie per il tuo acquisto.',
    path: '/checkout/success',
    noindex: true,
  });

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const { clearCart } = useCart();

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) { setStatus('error'); return; }
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (e) {
        if (attempts < 5) setTimeout(() => setAttempts((a) => a + 1), 2000);
        else setStatus('error');
      }
    };
    checkStatus();
  }, [sessionId, attempts, clearCart]);

  return (
    <div className="bg-ivory pt-24 md:pt-28 min-h-screen flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg mx-auto px-6 py-20 text-center">
        {status === 'checking' && (
          <div data-testid="status-checking">
            <div className="w-12 h-12 border-2 border-line border-t-bordeaux rounded-full animate-spin mx-auto mb-8" />
            <h1 className="font-display text-h3 text-ink mb-4">Verifica del pagamento in corso…</h1>
            <p className="text-muted leading-relaxed">Stiamo confermando il tuo pagamento. Attendere qualche istante.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="status-success">
            <div className="w-16 h-16 border border-bordeaux rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-8 h-8 text-bordeaux" strokeWidth={1.25} />
            </div>
            <h1 className="font-display text-h2 text-ink mb-4">Ordine Confermato</h1>
            <p className="text-muted leading-relaxed mb-10 max-w-md mx-auto">
              Grazie per il tuo acquisto. Riceverai una email di conferma con tutti i dettagli del tuo ordine.
            </p>
            <div className="border border-line p-6 mb-10 text-left flex items-center gap-4">
              <Package className="w-8 h-8 text-bordeaux flex-shrink-0" strokeWidth={1.25} />
              <div>
                <p className="font-display text-h4 text-ink">Spedizione in preparazione</p>
                <p className="text-small text-muted mt-1">Il tuo ordine sarà spedito entro 2-3 giorni lavorativi.</p>
              </div>
            </div>
            <Link to="/collezione" data-testid="continue-shopping">
              <Button size="lg" variant="default">
                Continua lo Shopping
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        )}

        {status === 'pending' && (
          <div data-testid="status-pending">
            <div className="w-16 h-16 border border-line rounded-full flex items-center justify-center mx-auto mb-8">
              <Package className="w-7 h-7 text-bordeaux" strokeWidth={1.25} />
            </div>
            <h1 className="font-display text-h3 text-ink mb-4">Pagamento in elaborazione</h1>
            <p className="text-muted leading-relaxed mb-10">
              Il tuo pagamento è in fase di elaborazione. Riceverai una email di conferma appena completato.
            </p>
            <Link to="/" data-testid="go-home"><Button size="lg">Torna alla Home</Button></Link>
          </div>
        )}

        {(status === 'error' || status === 'expired') && (
          <div data-testid="status-error">
            <div className="w-16 h-16 border border-line rounded-full flex items-center justify-center mx-auto mb-8">
              <XCircle className="w-8 h-8 text-muted" strokeWidth={1.25} />
            </div>
            <h1 className="font-display text-h3 text-ink mb-4">
              {status === 'expired' ? 'Sessione scaduta' : 'Errore'}
            </h1>
            <p className="text-muted leading-relaxed mb-10">
              {status === 'expired'
                ? 'La sessione di pagamento è scaduta. Per favore, riprova dal carrello.'
                : 'Si è verificato un errore durante la verifica del pagamento.'}
            </p>
            <Link to="/carrello" data-testid="retry-checkout">
              <Button size="lg" variant="accent">Torna al Carrello</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
